import { X, Printer } from "lucide-react";
import { createPortal } from "react-dom";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return (Math.round(n * 10000) / 10000).toLocaleString("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

export default function ReconciliationPrintReport({ data, year, month, onClose }) {
  const handlePrint = () => window.print();
  return (
    <>
      <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-neutral-900">Previzualizare reconciliere</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Reconciliere lunară · {MONTHS_RO[month - 1]} {year} (A4)</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="recon-print-confirm" onClick={handlePrint} className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors">
                <Printer className="w-3.5 h-3.5" /> Tipărește
              </button>
              <button data-testid="recon-print-close" onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-1 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto thin-scroll p-6 bg-neutral-100">
            <div className="bg-white shadow-sm mx-auto" style={{ maxWidth: "297mm", padding: "12mm" }}>
              <ReconBody data={data} year={year} month={month} />
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <div className="hidden print:block print-area">
          <ReconBody data={data} year={year} month={month} />
        </div>,
        document.body
      )}
    </>
  );
}

function ReconBody({ data, year, month }) {
  const rows = data?.rows || [];
  const t = data?.totals || {};
  const shown = rows.filter((r) => r.initial || r.entries || r.consumption || r.has_physical);
  const fapticM = (data?.faptic_month || (month === 12 ? 1 : month + 1)) - 1;
  const fapticY = data?.faptic_year || (month === 12 ? year + 1 : year);
  return (
    <div className="text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="flex items-end justify-between mb-5 pb-3 border-b-2 border-neutral-900 print-avoid-break">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reconciliere Lunară Materii Prime</h1>
          <p className="text-xs text-neutral-600 mt-0.5">Brutăria LaDenis · Stoc inițial + Intrări − Consum vs. inventar faptic</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-neutral-500 uppercase tracking-wider">Lună</p>
          <p className="text-sm font-semibold">{MONTHS_RO[month - 1]} {year}</p>
          <p className="text-[10px] text-neutral-500 mt-0.5">Faptic: inventar {MONTHS_RO[fapticM]} {fapticY}</p>
        </div>
      </div>

      <table className="print-table w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="border border-neutral-700 px-2 py-1 text-left w-6">#</th>
            <th className="border border-neutral-700 px-2 py-1 text-left">Materie primă</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-20">Stoc inițial</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-20">+ Intrări</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-20">= Total</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-20">− Consum</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-24">Stoc final teoretic</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-24">Inventar faptic</th>
            <th className="border border-neutral-700 px-2 py-1 text-right w-20">Diferență</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, idx) => (
            <tr key={r.name}>
              <td className="border border-neutral-400 px-2 py-1 text-neutral-500">{idx + 1}</td>
              <td className="border border-neutral-400 px-2 py-1 capitalize">{r.name}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{fmt(r.initial)}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{fmt(r.entries)}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-semibold">{fmt(r.total)}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums text-amber-700">{fmt(r.consumption)}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums font-semibold">{fmt(r.theoretical_final)}</td>
              <td className="border border-neutral-400 px-2 py-1 text-right tabular-nums">{r.has_physical ? fmt(r.physical_final) : "—"}</td>
              <td className={`border border-neutral-400 px-2 py-1 text-right tabular-nums font-semibold ${
                r.diff === null ? "text-neutral-400" :
                Math.abs(r.diff) < 0.001 ? "text-neutral-500" :
                r.diff > 0 ? "text-emerald-700" : "text-red-700"
              }`}>
                {r.diff === null ? "—" : (r.diff > 0 ? `+${fmt(r.diff)}` : fmt(r.diff))}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} className="border border-neutral-700 px-2 py-1 font-bold text-right uppercase tracking-wider">Total (kg)</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.initial)}</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.entries)}</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.total)}</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.consumption)}</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.theoretical_final)}</td>
            <td className="border border-neutral-700 px-2 py-1 text-right tabular-nums font-bold">{fmt(t.physical_final)}</td>
            <td className="border border-neutral-700 px-2 py-1"></td>
          </tr>
        </tfoot>
      </table>

      <p className="text-[10px] text-neutral-500 mt-2 italic">
        Total = Stoc inițial + Intrări · Stoc final teoretic = Total − Consum · Diferență = Inventar faptic − Stoc final teoretic
        (<span className="text-emerald-700">+ = plus</span>, <span className="text-red-700">− = minus/lipsă</span>).
        {!data?.has_faptic && " · Inventarul faptic apare după ce salvezi stocul inițial (inventarul de azi)."}
      </p>

      <div className="mt-8 pt-4 border-t border-neutral-300 grid grid-cols-3 gap-6 text-xs text-neutral-600 print-avoid-break">
        <div><p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Întocmit</p><div className="border-b border-neutral-400 h-6"></div></div>
        <div><p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Verificat</p><div className="border-b border-neutral-400 h-6"></div></div>
        <div><p className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px] mb-1">Aprobat</p><div className="border-b border-neutral-400 h-6"></div></div>
      </div>
    </div>
  );
}
